import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Modal, Form, Spinner, Badge, Alert } from 'react-bootstrap';
import { Plus, Edit, Trash2, CreditCard, Building } from 'lucide-react';
import Sidebar from '../components/Layout/Sidebar';
import api from '../services/api';
import './PaymentAccountsPage.css';
const PaymentAccountsPage = () => {
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [permissions, setPermissions] = useState({ canView: true, canInsert: false, canEdit: false, canDelete: false });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAccount, setCurrentAccount] = useState({
    acc_id: null, acc_name: '', bank_info: '', acc_number: '',
    acc_address: '', swift: '', vat: 'no VAT', post_office_address: '', service_type: 1
  });
  useEffect(() => {
    loadPaymentAccounts();
  }, []);
  const loadPaymentAccounts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/payment-accounts');
const data = response.data.paymentAccounts ? response.data : { paymentAccounts: response.data, permissions: { canView: true, canInsert: false, canEdit: false, canDelete: false } };
setPaymentAccounts(data.paymentAccounts || []);
setPermissions(data.permissions || { canView: true, canInsert: false, canEdit: false, canDelete: false });
    } catch (error) {
      console.error('Error loading payment accounts:', error);
      alert('Failed to load payment accounts');
    } finally {
      setLoading(false);
    }
  };
  const handleSave = async () => {
    try {
      if (editMode) {
        await api.put(`/payment-accounts/${currentAccount.acc_id}`, currentAccount);
      } else {
        await api.post('/payment-accounts', currentAccount);
      }
      loadPaymentAccounts();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving payment account:', error);
      alert(error.response?.data?.message || 'Failed to save payment account');
    }
  };
  const handleDelete = async (acc_id) => {
    if (window.confirm('Delete this payment account?')) {
      try {
        await api.delete(`/payment-accounts/${acc_id}`);
        loadPaymentAccounts();
      } catch (error) {
        console.error('Error deleting payment account:', error);
        alert(error.response?.data?.message || 'Failed to delete payment account');
      }
    }
  };
  const resetForm = () => {
    setCurrentAccount({
      acc_id: null, acc_name: '', bank_info: '', acc_number: '',
      acc_address: '', swift: '', vat: 'no VAT', post_office_address: '', service_type: 1
    });
    setEditMode(false);
  };
  const handleEdit = (account) => {
    setCurrentAccount(account);
    setEditMode(true);
    setShowModal(true);
  };
  const getStatusBadge = (verified, def) => {
    if (verified === 1) return <Badge bg="success">Verified</Badge>;
    return <Badge bg="warning">Pending</Badge>;
  };
  return (
    <div className="d-flex">
      <Sidebar />
      <Container fluid className="payment-accounts-page p-4">
        <div className="page-header">
          <div>
            <h2 className="page-title">Payment Accounts</h2>
            <p className="page-subtitle">Manage your bank and payment information</p>
          </div>
          {permissions.canInsert && (
            <Button variant="primary" onClick={() => { resetForm(); setShowModal(true); }} className="add-button">
              <Plus size={18} className="me-2" />
              Add Account
            </Button>
          )}
        </div>
        {!permissions.canView && (
          <Alert variant="warning">You don't have permission to view payment accounts</Alert>
        )}
        {permissions.canView && loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-3">Loading payment accounts...</p>
          </div>
        ) : permissions.canView && paymentAccounts.length === 0 ? (
          <Alert variant="info">No payment accounts found. {permissions.canInsert && 'Click "Add Account" to create your first account.'}</Alert>
        ) : permissions.canView && (
          <div className="table-container">
            <Table hover responsive className="payment-accounts-table">
              <thead>
                <tr>
                  <th>Account Name</th>
                  <th>Bank Info</th>
                  <th>Account Number</th>
                  <th>SWIFT</th>
                  <th>VAT</th>
                  <th>Status</th>
                  <th>Added</th>
                  {(permissions.canEdit || permissions.canDelete) && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {paymentAccounts.map((account) => (
                  <tr key={account.acc_id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <CreditCard size={18} className="me-2 text-primary" />
                        <strong>{account.acc_name}</strong>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <Building size={16} className="me-2 text-muted" />
                        {account.bank_info}
                      </div>
                    </td>
                    <td><code>{account.acc_number}</code></td>
                    <td>{account.swift || '-'}</td>
                    <td>{account.vat || '-'}</td>
                    <td>
                      {getStatusBadge(account.verified, account.def)}
                      {account.def === 1 && <Badge bg="primary" className="ms-1">Active</Badge>}
                    </td>
                    <td>{new Date(account.add_date).toLocaleDateString()}</td>
                    {(permissions.canEdit || permissions.canDelete) && (
                      <td>
                        <div className="action-buttons">
                          {permissions.canEdit && (
                            <Button size="sm" variant="outline-primary" onClick={() => handleEdit(account)} className="me-1">
                              <Edit size={14} />
                            </Button>
                          )}
                          {permissions.canDelete && (
                            <Button size="sm" variant="outline-danger" onClick={() => handleDelete(account.acc_id)}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? 'Edit Payment Account' : 'Add New Payment Account'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Account Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Main Business Account"
                  value={currentAccount.acc_name}
                  onChange={(e) => setCurrentAccount({ ...currentAccount, acc_name: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Bank Info *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Bank name and details"
                  value={currentAccount.bank_info}
                  onChange={(e) => setCurrentAccount({ ...currentAccount, bank_info: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Account Number *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter account number"
                  value={currentAccount.acc_number}
                  onChange={(e) => setCurrentAccount({ ...currentAccount, acc_number: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Account Address *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Bank address"
                  value={currentAccount.acc_address}
                  onChange={(e) => setCurrentAccount({ ...currentAccount, acc_address: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>SWIFT/BIC Code</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., CHASUS33"
                  value={currentAccount.swift}
                  onChange={(e) => setCurrentAccount({ ...currentAccount, swift: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>VAT</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., no VAT"
                  value={currentAccount.vat}
                  onChange={(e) => setCurrentAccount({ ...currentAccount, vat: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Post Office Address</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Postal address"
                  value={currentAccount.post_office_address}
                  onChange={(e) => setCurrentAccount({ ...currentAccount, post_office_address: e.target.value })}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSave}>{editMode ? 'Update' : 'Create'}</Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};
export default PaymentAccountsPage;